/**
 * React context provider that manages WebSocket connections.
 *
 * - Creates a studio-wide WsClient when the user is authenticated.
 * - Exposes helpers to subscribe/unsubscribe to event-specific channels.
 * - Tears down all connections on logout.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

import { useAuthStore } from '@/store/authStore';
import { useWsStore } from '@/store/wsStore';
import { WsClient, type WsConnectionStatus } from './wsClient';
import { handleWsMessage } from './wsHandlers';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface WebSocketContextValue {
  /** Subscribe to real-time updates for a specific event. */
  subscribeToEvent: (eventId: string) => void;
  /** Unsubscribe from a specific event channel. */
  unsubscribeFromEvent: (eventId: string) => void;
  /** Current connection status of the studio notification channel. */
  connectionStatus: WsConnectionStatus;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  subscribeToEvent: () => {},
  unsubscribeFromEvent: () => {},
  connectionStatus: 'disconnected',
});

export function useWebSocket() {
  return useContext(WebSocketContext);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
}

export function WebSocketProvider({ children }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { connectionStatus, setConnectionStatus } = useWsStore();

  // Refs keep mutable state across renders without triggering re-renders.
  const studioClient = useRef<WsClient | null>(null);
  const eventClients = useRef<Map<string, WsClient>>(new Map());

  // -------------------------------------------------------------------
  // Studio notification channel
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      // Tear down on logout
      studioClient.current?.disconnect();
      studioClient.current = null;
      eventClients.current.forEach((c) => c.disconnect());
      eventClients.current.clear();
      setConnectionStatus('disconnected');
      return;
    }

    const client = new WsClient('/ws/notifications/', {
      token: accessToken,
      onMessage: handleWsMessage,
      onStatusChange: setConnectionStatus,
    });
    client.connect();
    studioClient.current = client;

    return () => {
      client.disconnect();
    };
  }, [isAuthenticated, accessToken, setConnectionStatus]);

  // -------------------------------------------------------------------
  // Event subscriptions
  // -------------------------------------------------------------------
  const subscribeToEvent = useCallback(
    (eventId: string) => {
      if (!accessToken || eventClients.current.has(eventId)) return;

      const client = new WsClient(`/ws/analysis/${eventId}/`, {
        token: accessToken,
        onMessage: handleWsMessage,
      });
      client.connect();
      eventClients.current.set(eventId, client);
    },
    [accessToken],
  );

  const unsubscribeFromEvent = useCallback((eventId: string) => {
    const client = eventClients.current.get(eventId);
    if (client) {
      client.disconnect();
      eventClients.current.delete(eventId);
    }
  }, []);

  // Clean up everything on unmount
  useEffect(() => {
    return () => {
      studioClient.current?.disconnect();
      eventClients.current.forEach((c) => c.disconnect());
    };
  }, []);

  return (
    <WebSocketContext.Provider
      value={{ subscribeToEvent, unsubscribeFromEvent, connectionStatus }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}
